<template>
    <div>
        <Panel
            :title="$t('SIP Trunks')"
            :add-button="$store.hasRight('ADD_SIP_TRUNKS') ? $t('New SIP Trunk') : ''"
            :delete-button="selected.length > 0"
            @delete="handleDelete"
            @input="openForm(-1)"
        >
            <table id="siptrunks_table" class="cic-table">
                <thead>
                    <tr>
                        <th>{{ $t('SIP Trunk') }}</th>
                        <th>{{ $t('Domain') }}</th>
                        <th>{{ $t('Port') }}</th>
                        <th v-if="$store.hasRight('DELETE_SIP_TRUNKS')" class="td-checkbox"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="trunk in items" @click="(evt) => handleOpen(trunk.trunkId, evt)">
                        <td class="primary">{{ trunk.name }}</td>
                        <td>{{ trunk.domain }}</td>
                        <td>{{ trunk.port }}</td>
                        <td v-if="$store.hasRight('DELETE_SIP_TRUNKS')">
                            <CicCheckbox
                                :value="selected.includes(trunk.trunkId)"
                                @click="handleSelected(trunk.trunkId)"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </Panel>

        <EditSipTrunk
            v-if="item !== null && !$store.showLoadingOverlay"
            :item="item"
            :external-apps="
                item.breakout && item.externalServiceId && apps[`_${item.externalServiceId}`]
                    ? apps[`_${item.externalServiceId}`]
                    : []
            "
            :internal-apps="
                item.breakout && item.internalServiceId && apps[`_${item.internalServiceId}`]
                    ? apps[`_${item.internalServiceId}`]
                    : []
            "
            :services="services"
            @blur="closePopup"
            @change="updateFieldValue"
            @input="saveItem"
        />

        <div v-if="$store.showLoadingOverlay" class="cic-overlay"></div>
    </div>
</template>

<script>
    import Panel from '@/components/container/Panel';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import TableUtils from '@/mixins/TableUtils';
    import { deleteItems, load, save } from '@/utils/Http';
    import EditSipTrunk from './EditSipTrunk';

    export default {
        name: 'SipTrunks',
        components: {
            CicCheckbox,
            EditSipTrunk,
            Panel,
        },
        mixins: [TableUtils],
        data() {
            return {
                apps: {},
                item: null,
                items: [],
                page: 1,
                selected: [],
                services: [],
            };
        },
        created() {
            this.loadTable();
        },
        methods: {
            handleDelete() {
                deleteItems('siptrunks/delete', { trunkIds: this.selected }, this.loadTable);
            },

            loadApplications(serviceId) {
                if (!this.apps[`_${serviceId}`]) {
                    load('siptrunk/applications', { serviceId }, (response) => {
                        this.apps[`_${serviceId}`] = response.applications.map((app) => ({ value: app, label: app }));
                    });
                }
            },

            loadServices() {
                if (this.services.length === 0) {
                    load('siptrunk/services', {}, (response) => {
                        this.services = response.services.map((service) => ({
                            value: service.serviceId,
                            label: service.name,
                        }));
                    });
                }
            },

            loadTable() {
                this.item = null;
                this.selected = [];
                load('siptrunks/get', {}, (response) => {
                    this.items = response.trunks;
                });
            },

            openForm(trunkId) {
                this.item = null;

                if (trunkId > 0) {
                    load('siptrunk/get', { trunkId }, (response) => {
                        this.item = response.trunk;
                        if (this.$store.hasRight('EDIT_SIP_BREAKOUT_SERVICE') && this.item.breakout) {
                            this.loadServices();
                            this.loadApplications(this.item.externalServiceId);
                            this.loadApplications(this.item.internalServiceId);
                        }
                    });
                } else {
                    this.item = {
                        trunkId: null,
                        name: '',
                        domain: null,
                        port: 0,
                        breakout: false,
                        externalServiceId: null,
                        externalApp: null,
                        internalServiceId: null,
                        internalApp: null,
                        digits: null,
                        stopRecording: false,
                    };
                }
            },

            saveItem() {
                if (this.item.name.trim() === '') {
                    alert(this.$t('Name is a required field'));
                    return;
                }

                if (
                    (!this.item.trunkId || this.$store.hasRight('EDIT_UNREGISTERED_SIP_TRUNKS')) &&
                    (!this.item.domain || this.item.domain.trim() === '')
                ) {
                    alert(this.$t('Domain is a required field'));
                    return;
                }

                const params = {
                    trunkId: this.item.trunkId,
                    name: this.item.name,
                    domain: this.item.domain,
                    port: this.item.port,
                };

                if (this.$store.hasRight('EDIT_SIP_BREAKOUT_SERVICE') && this.item.breakout) {
                    if (!this.item.externalServiceId) {
                        alert(this.$t('Invalid External Service'));
                        return;
                    }
                    if (!this.item.internalServiceId) {
                        alert(this.$t('Invalid Internal Service'));
                        return;
                    }
                    if (!this.item.externalApp) {
                        alert(this.$t('Invalid External Application'));
                        return;
                    }
                    if (!this.item.internalApp) {
                        alert(this.$t('Invalid Internal Application'));
                        return;
                    }
                    if (!this.item.digits || this.item.digits.trim() === '') {
                        alert(this.$t('Breakout Digits is a required field'));
                        return;
                    }

                    params.externalServiceId = this.item.externalServiceId;
                    params.externalApp = this.item.externalApp;
                    params.internalServiceId = this.item.internalServiceId;
                    params.internalApp = this.item.internalApp;
                    params.digits = this.item.digits;
                    params.stopRecording = this.item.stopRecording ? 1 : 0;
                }

                save('siptrunk/update', params, () => {
                    this.loadTable();
                });
            },

            updateFieldValue(field, value) {
                let item = { ...this.item };
                item[field] = value;
                if (field === 'breakout' && value) {
                    this.loadServices();
                }
                if (field === 'externalServiceId') {
                    item.externalApp = null;
                    if (value) {
                        this.loadApplications(value);
                    }
                }
                if (field === 'internalServiceId') {
                    item.internalApp = null;
                    if (value) {
                        this.loadApplications(value);
                    }
                }

                this.item = item;
            },
        },
    };
</script>
